import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { ProfileSectionKey } from "../pages/patient-profile/profileSections";

/**
 * Each patient has one profile workspace that stays mounted while other tabs
 * front. The URL (`/patients/$patientId?section=`) selects the active profile;
 * each open tab remembers its own section and label within Patients.
 */
export interface PatientProfileTab {
  readonly patientId: string;
  /** Display name once known (directory or the loaded profile); null until then. */
  readonly label: string | null;
  readonly section: ProfileSectionKey;
}

interface PatientProfileTabContextValue {
  readonly tabs: readonly PatientProfileTab[];
  /** Opens or reuses this patient's tab; other patients retain their own state. */
  openProfileTab(
    patientId: string,
    options?: {
      readonly label?: string;
      readonly section?: ProfileSectionKey;
    },
  ): void;
  setProfileTabLabel(patientId: string, label: string): void;
  setProfileTabSection(patientId: string, section: ProfileSectionKey): void;
  closeProfileTab(patientId: string): void;
  clearProfileTabs(): void;
}

const PatientProfileTabContext = createContext<PatientProfileTabContextValue | null>(null);

export function PatientProfileTabProvider({ children }: { readonly children: ReactNode }) {
  const [tabs, setTabs] = useState<readonly PatientProfileTab[]>([]);

  const openProfileTab = useCallback<PatientProfileTabContextValue["openProfileTab"]>(
    (patientId, options = {}) => {
      setTabs((current) => {
        const existing = current.find((tab) => tab.patientId === patientId);
        const section = options.section ?? existing?.section ?? "overview";
        const label = options.label ?? existing?.label ?? null;
        const unchanged = existing?.section === section && existing?.label === label;
        const next = unchanged ? existing : { patientId, section, label };
        if (unchanged) return current;
        return existing === undefined
          ? [...current, next]
          : current.map((tab) => (tab.patientId === patientId ? next : tab));
      });
    },
    [],
  );

  const setProfileTabLabel = useCallback((patientId: string, label: string) => {
    setTabs((current) => {
      if (!current.some((tab) => tab.patientId === patientId && tab.label !== label))
        return current;
      return current.map((tab) => (tab.patientId === patientId ? { ...tab, label } : tab));
    });
  }, []);

  const setProfileTabSection = useCallback((patientId: string, section: ProfileSectionKey) => {
    setTabs((current) => {
      if (!current.some((tab) => tab.patientId === patientId && tab.section !== section))
        return current;
      return current.map((tab) => (tab.patientId === patientId ? { ...tab, section } : tab));
    });
  }, []);

  const closeProfileTab = useCallback((patientId: string) => {
    setTabs((current) => current.filter((tab) => tab.patientId !== patientId));
  }, []);
  const clearProfileTabs = useCallback(() => setTabs([]), []);

  const value = useMemo(
    () => ({
      tabs,
      openProfileTab,
      setProfileTabLabel,
      setProfileTabSection,
      closeProfileTab,
      clearProfileTabs,
    }),
    [
      tabs,
      openProfileTab,
      setProfileTabLabel,
      setProfileTabSection,
      closeProfileTab,
      clearProfileTabs,
    ],
  );

  return (
    <PatientProfileTabContext.Provider value={value}>{children}</PatientProfileTabContext.Provider>
  );
}

export function usePatientProfileTab(): PatientProfileTabContextValue {
  const context = useContext(PatientProfileTabContext);
  if (!context) {
    throw new Error("usePatientProfileTab must be used inside PatientProfileTabProvider");
  }
  return context;
}
